import { LibraryCache } from 'base/library_cache';
import { computedFn } from 'mobx-utils';
import {
  ElementInstance,
  Page,
  pageDomain,
  Project,
  SectionRef,
} from '../model';
import {
  findParentSpace,
  firstSelectableAncestorElementWithinRoot,
  parentsMap,
  unrefSection,
} from '../model_helpers';
import { SelectionState } from './states';

export class SelectionFactory {
  constructor(private readonly project: Project) {}

  readonly forPage = computedFn((page: Page) => {
    return { ...this.parentOfPage(page), page } satisfies SelectionState;
  });

  readonly parentOfPage = computedFn((page: Page) => {
    return {
      space: findParentSpace(this.project, page) || 'home',
    } satisfies SelectionState;
  });

  /** The selection if you press `Esc` with `page` selected.  Assumes the pages are displayed in a tree view */
  readonly treeParentOfPage = computedFn((page: Page): SelectionState => {
    const parent = parentsMap(this.project).get(page);
    const { space } = this.parentOfPage(page);
    if (parent == null) {
      return {};
    } else if (pageDomain.isMutable(parent)) {
      return { space, page: parent };
    } else {
      return { space };
    }
  });

  readonly forSection = computedFn((section: SectionRef) => {
    return {
      ...this.parentOfSectionRef(section),
      section,
    } satisfies SelectionState;
  });

  readonly parentOfSectionRef = computedFn((section: SectionRef) => {
    const page =
      sectionRefParentsMap(this.project).get(section) || this.project.homePage;
    return this.forPage(page);
  });

  readonly parentOfElementNode = computedFn(
    (
      sel: SelectionState & {
        element: ElementInstance;
      },
      library: LibraryCache,
    ) => {
      const section = unrefSection(sel.section, this.project);
      const rootElement = section?.element.current;
      const parentElement =
        rootElement &&
        firstSelectableAncestorElementWithinRoot(
          sel.element,
          rootElement,
          library,
        );
      if (sel.nodeId) {
        if (sel.element === rootElement) {
          // Do not allow people to select the root element
          return { ...sel, element: undefined, nodeId: undefined };
        } else {
          return { ...sel, nodeId: undefined };
        }
      } else if (parentElement && parentElement !== rootElement) {
        return { ...sel, element: parentElement, nodeId: undefined };
      } else {
        return { ...sel, element: undefined, nodeId: undefined };
      }
    },
  );
  readonly isRootPageOfSpace = computedFn((page: Page) => {
    return this.treeParentOfPage(page).page === undefined;
  });
}

/** Parent pages for each section within a project */
export const sectionRefParentsMap = computedFn(
  (project: Project): ReadonlyMap<SectionRef, Page> => {
    const output = new Map<SectionRef, Page>();

    function walk(page: Page) {
      for (const section of page.sections) {
        output.set(section, page);
      }
      for (const child of page.subPages) {
        walk(child);
      }
    }

    walk(project.homePage);
    for (const space of project.spaces) {
      space.subPages.forEach(walk);
    }
    return output;
  },
);
